import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { UploadDownloadService } from '@shared/services/upload-download.service';
import { openS3File } from '@app/shared/utils/open-s3-file';
import { LogisticsCatalogs, fmtDate } from '../logistics.service';

/** Detalle de un resguardo / devolución (piezas, folio de origen, fotos). */
@Component({
  selector: 'app-custody-detail-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule],
  styleUrl: '../logistics-dialog.scss',
  styles: [`.wrap { display: grid; gap: 10px; min-width: min(760px, 86vw); } .meta { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 6px 14px; font-size: 13px; } .meta b { color: #1F3C88; font-weight: 600; }`],
  template: `
<h2 mat-dialog-title>{{ c.folio }} · {{ c.type === 'RESGUARDO' ? 'Resguardo' : 'Devolución extraordinaria' }}</h2>
<mat-dialog-content>
  <div class="wrap">
    <div class="meta">
      <span><b>Supervisor / coordinador:</b> {{ c.supervisorName || '—' }}</span>
      <span><b>Alias:</b> {{ cat.placeName(c.place) || '—' }}</span>
      <span><b>Locker:</b> {{ cat.warehousesById[c.warehouse]?.name || '—' }}</span>
      <span><b>Fecha:</b> {{ fmtDate(c.createdOn) }}</span>
      <span><b>Estado:</b> {{ c.status }}</span>
      @if (c.type === 'RESGUARDO') { <span><b>Responsable:</b> {{ c.responsibleName }}{{ c.responsibleArea ? ' · ' + c.responsibleArea : '' }}</span>
        <span><b>Fecha compromiso:</b> {{ commitment }}</span> }
      <span><b>Aviso enviado:</b> {{ c.notifiedOn ? fmtDate(c.notifiedOn) : 'No' }}</span>
      @if (c.closedOn) { <span><b>Cerrado:</b> {{ fmtDate(c.closedOn) }}</span> }
    </div>
    <div class="pieces">
      <table>
        <thead><tr><th>Tipo</th><th>Código</th><th>Descripción</th><th>Lote</th><th>Pzas</th><th>Etiqueta / hallazgo</th></tr></thead>
        <tbody>
          @for (it of c.items ?? []; track $index) {
            <tr [class.is-flag]="it.otherAccount || !(it.tagged || it.stock)">
              <td>{{ it.tagged || it.stock ? 'Etiquetado' : 'Sin etiqueta' }}</td>
              <td>{{ it.code || '—' }}</td><td>{{ it.description || '—' }}</td><td>{{ it.batch || '—' }}</td><td>{{ it.quantity || 1 }}</td>
              <td>
                @if (it.tagRfid) { <span class="tag">{{ it.tagRfid }}</span> }
                @if (c.type === 'RESGUARDO' && !it.stock) {
                  <span class="sub flag">Nunca estuvo en sistema</span>
                  @if (it.photo) { <button mat-button type="button" (click)="open(it.photo)"><mat-icon>photo</mat-icon> Ver foto</button> }
                }
                @if (c.type === 'DEVOLUCION') {
                  @if (it.tagged) {
                    <span class="sub">Folio {{ it.foundFolio || '—' }}</span>
                    @if (it.otherAccount) { <span class="sub flag">De otra cuenta: {{ it.foundPlaceName }}</span> }
                  } @else {
                    <span class="sub flag">Nunca estuvo en sistema</span>
                    @if (it.photo) { <button mat-button type="button" (click)="open(it.photo)"><mat-icon>photo</mat-icon> Ver foto</button> }
                  }
                }
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
    @if (c.notes) { <div class="hint-box"><mat-icon>notes</mat-icon><span>{{ c.notes }}</span></div> }
  </div>
</mat-dialog-content>
<mat-dialog-actions align="end"><button mat-button mat-dialog-close>Cerrar</button></mat-dialog-actions>
  `,
})
export class CustodyDetailDialogComponent {
  readonly c = inject<{ record: any }>(MAT_DIALOG_DATA).record;
  readonly cat = inject(LogisticsCatalogs);
  readonly commitment = ((m) => m ? `${m[3]}/${m[2]}/${m[1]}` : '—')(/^(\d{4})-(\d{2})-(\d{2})/.exec(String(this.c?.commitmentDate ?? '')));
  readonly fmtDate = fmtDate;
  private readonly uploads = inject(UploadDownloadService);
  private readonly msg = inject(CustomMessageService);

  async open(key: string): Promise<void> {
    const err = await openS3File(this.uploads, key);
    if (err) await this.msg.open({ title: 'Foto', type: 'warning', message: err, dismissBtn: true });
  }
}
